// ============================================================================
// PlayerPropsCard — anytime-goalscorer list for the match
// ============================================================================
// A ranked player list (favourite first) — a genuinely different shape from
// the 2-3 outcome market cards in MultiMarketStack, so it gets its own small
// widget rather than being forced into the market-card path. Fed by the
// engine's `playerProps` on the market-layouts response (built from the provider
// "To Score" offers). Self-hides when the book prices no player props.
//
// Own one-shot GET (no warm/poll). Isolated under an ErrorBoundary in
// MatchDetailScreen. Heading is localised; player names are data.
// ============================================================================

import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useEngineClient } from '../../api/EngineClientProvider.js';
import OddsMarket from '../particles/OddsMarket.js';
import type { DisplayMarket } from '../../types/odds.js';
import styles from './PlayerPropsCard.module.css';

interface Props {
  eventId: string;
}

/** One priced player from the engine's `playerProps` envelope. */
export interface PlayerProp {
  name: string;
  price: number;
}

export function PlayerPropsCard({ eventId }: Props): ReactElement | null {
  const client = useEngineClient();
  const [players, setPlayers] = useState<PlayerProp[] | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    const url = `/onsite/feed/${encodeURIComponent(client.slug)}/match/${encodeURIComponent(eventId)}/market-layouts`;
    fetch(url, { headers: { Accept: 'application/json' }, signal: ctrl.signal, credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: unknown) => {
        // Narrow the dynamic feed JSON honestly — no `any` (T3 lint audit).
        const root = j !== null && typeof j === 'object' ? (j as Record<string, unknown>) : null;
        const props =
          root && root.playerProps !== null && typeof root.playerProps === 'object'
            ? (root.playerProps as Record<string, unknown>)
            : null;
        const pp = props?.players;
        if (Array.isArray(pp) && pp.length) {
          setPlayers(pp as PlayerProp[]);
        }
      })
      .catch(() => { /* isolated — never surface */ });
    return (): void => ctrl.abort();
  }, [client.slug, eventId]);

  if (!players || players.length === 0) return null;

  return <PlayerPropsCardView players={players} />;
}

/**
 * Presentational card — split from the fetching container so Storybook
 * and tests can render the exact production markup without a network.
 *
 * ADR-059: odds display belongs to the OddsMarket particle. This widget
 * supplies only the card shell + heading; the ranked player rows render
 * through `OddsMarket appearance="list"` so player prices sit on the
 * same brand contract as every other odds surface.
 */
export function PlayerPropsCardView({
  players,
}: {
  players: PlayerProp[];
}): ReactElement | null {
  const { t } = useTranslation();

  if (players.length === 0) return null;

  const heading = t('oddsBoard.anytimeScorer', 'Anytime goalscorer');

  const market: DisplayMarket = {
    key: 'player-props',
    title: heading,
    layout: 'stack',
    outcomes: players.map((p, i) => ({
      id: i,
      label: p.name,
      odds: p.price,
      type: 'OT_YES',
      status: 'OPEN',
    })),
  };

  return (
    <section className={styles.card} aria-label={heading}>
      <h2 className={styles.heading}>{heading}</h2>
      {/* hideTitle — the <h2> above is the card's heading; OddsMarket's
          own header would duplicate it. */}
      <OddsMarket market={market} appearance="list" hideTitle />
    </section>
  );
}

export default PlayerPropsCard;
